/**
 * GH#2768 — ¿el token de la sesión activa es una SUPLANTACIÓN?
 *
 * El api marca el token que acuña `POST /admin/users/:id/impersonate` con el claim `impersonatedBy` (auth.service.ts, `generateAccessToken`).
 * Ese claim SOBREVIVE a la recarga de la página; el estado `isImpersonating` de Redux no (impersonation-slice.ts: solo en memoria).
 *
 * ES UNA PISTA DE INTERFAZ, NO UNA FRONTERA DE SEGURIDAD:
 *  - se lee el payload SIN verificar la firma (el navegador no tiene con qué): quien pueda escribir su propio localStorage puede escribir
 *    cualquier cosa ahí; el api valida el JWT en cada pedido y rechaza un token cuyo payload no coincida con su firma;
 *  - SOLO sirve para dos cosas: no mandar a la persona al onboarding de la cuenta que está viendo, y mostrar la barra «Volver a Super Admin»;
 *  - cualquier cosa que no se pueda leer con certeza devuelve `null`, y `null` significa «comportamiento de siempre»: esta función falla hacia
 *    lo que había antes (mandar a /onboarding, sin barra), NUNCA hacia saltarse el guard.
 */
export interface SuplantacionDelToken {
  /** id del super-admin que abrió la suplantación (claim `impersonatedBy`). */
  impersonatedBy: string;
}

function decodificarPayload(token: string): unknown {
  const partes = token.split('.');
  if (partes.length !== 3 || !partes[1]) return null;
  const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
  const relleno = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binario = atob(relleno);
  const bytes = Uint8Array.from(binario, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

export function leerSuplantacionDelToken(token: unknown, ahoraMs: number = Date.now()): SuplantacionDelToken | null {
  try {
    if (typeof token !== 'string' || token === '') return null;
    const payload = decodificarPayload(token);
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const { impersonatedBy, exp } = payload as { impersonatedBy?: unknown; exp?: unknown };
    if (typeof impersonatedBy !== 'string' || impersonatedBy.trim() === '') return null;
    // Un token ya vencido no es una suplantación vigente: se trata como siempre (el api igual lo rechazaría).
    if (typeof exp === 'number' && Number.isFinite(exp) && exp * 1000 <= ahoraMs) return null;
    return { impersonatedBy };
  } catch {
    return null;
  }
}
